/* Peppers Hero Slider
   Eigenständige Vanilla-JS-Komponente je Section-Instanz. Ohne erfolgreiche Initialisierung bleibt der
   serverseitig gerenderte Zustand (Slide 1 sichtbar, Controls hidden) unverändert stehen. */

if (!customElements.get('peppers-hero-slider')) {
  class PeppersHeroSlider extends HTMLElement {
    connectedCallback() {
      try {
        this.init();
      } catch (error) {
        console.error('[peppers-hero-slider]', error);
        this.destroy();
      }
    }

    disconnectedCallback() {
      this.destroy();
    }

    init() {
      this.slides = Array.from(this.querySelectorAll('.peppers-hero-slider__slide'));
      if (this.slides.length < 2) return;

      this.viewport = this.querySelector('.peppers-hero-slider__viewport');
      this.dots = Array.from(this.querySelectorAll('[data-phs-dot]'));
      this.pauseButton = this.querySelector('[data-phs-pause]');
      this.status = this.querySelector('.peppers-hero-slider__status');

      const activeIndex = this.slides.findIndex((slide) => slide.classList.contains('is-active'));
      this.index = Math.max(0, activeIndex);
      this.autoplay = this.dataset.autoplay === 'true';
      this.speed = (Number(this.dataset.speed) || 6) * 1000;
      this.mode = this.dataset.transition === 'fade' ? 'fade' : 'slide';
      this.duration = Number(this.dataset.duration) || 600;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.canHover = window.matchMedia('(hover: hover)');
      this.pauseReasons = new Set();
      this.animations = [];
      this.animationId = 0;
      this.pendingCleanup = null;
      this.drag = null;
      this.peek = null;
      this.timer = null;
      this.abortController = new AbortController();

      if (this.reducedMotion.matches) this.pauseReasons.add('user');

      this.bindEvents();
      this.updateSlides();
      this.updatePauseButton();

      this.querySelectorAll('[data-phs-control]').forEach((control) => control.removeAttribute('hidden'));
      this.setAttribute('data-initialized', '');

      if (document.readyState === 'complete') {
        this.preload(this.index + 1);
      } else {
        window.addEventListener('load', () => this.preload(this.index + 1), {
          once: true,
          signal: this.abortController.signal,
        });
      }

      this.schedule();
    }

    destroy() {
      if (this.abortController) this.abortController.abort();
      if (this.observer) this.observer.disconnect();
      clearTimeout(this.timer);
      if (this.animations) this.animations.forEach((animation) => animation.cancel());
      this.animations = [];
      this.pendingCleanup = null;
      this.observer = null;
    }

    bindEvents() {
      const options = { signal: this.abortController.signal };

      this.querySelector('[data-phs-prev]')?.addEventListener('click', () => this.navigate(this.index - 1, -1), options);
      this.querySelector('[data-phs-next]')?.addEventListener('click', () => this.navigate(this.index + 1, 1), options);
      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => this.navigate(Number(dot.dataset.phsDot)), options);
      });
      this.pauseButton?.addEventListener('click', () => this.togglePause(), options);

      this.addEventListener('keydown', (event) => this.onKeydown(event), options);

      this.addEventListener(
        'mouseenter',
        () => {
          if (this.canHover.matches) this.pause('hover');
        },
        options
      );
      this.addEventListener('mouseleave', () => this.resume('hover'), options);

      this.addEventListener(
        'focusin',
        (event) => {
          let keyboardFocus = true;
          try {
            keyboardFocus = event.target.matches(':focus-visible');
          } catch (error) {}
          if (keyboardFocus) this.pause('focus');
        },
        options
      );
      this.addEventListener(
        'focusout',
        (event) => {
          if (!this.contains(event.relatedTarget)) this.resume('focus');
        },
        options
      );

      document.addEventListener(
        'visibilitychange',
        () => (document.hidden ? this.pause('hidden') : this.resume('hidden')),
        options
      );

      this.reducedMotion.addEventListener(
        'change',
        () => {
          if (this.reducedMotion.matches) this.pause('user');
          this.updatePauseButton();
        },
        options
      );

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => (entry.isIntersecting ? this.resume('offscreen') : this.pause('offscreen')));
          },
          { threshold: 0.25 }
        );
        this.observer.observe(this);
      }

      this.viewport.addEventListener('pointerdown', (event) => this.onPointerDown(event), options);
      this.viewport.addEventListener('pointermove', (event) => this.onPointerMove(event), options);
      this.viewport.addEventListener('pointerup', (event) => this.onPointerUp(event), options);
      this.viewport.addEventListener('pointercancel', (event) => this.onPointerUp(event, true), options);

      if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:block:select', (event) => this.onBlockSelect(event), options);
        document.addEventListener('shopify:block:deselect', (event) => this.onBlockDeselect(event), options);
      }
    }

    /* Navigation */

    navigate(target, direction) {
      this.goTo(target, { direction, announce: true });
    }

    goTo(target, { direction, instant = false, announce = false, offset = 0 } = {}) {
      const count = this.slides.length;
      const nextIndex = ((target % count) + count) % count;

      this.completeAnimations();

      if (nextIndex === this.index) {
        this.schedule();
        return;
      }

      const current = this.slides[this.index];
      const incoming = this.slides[nextIndex];
      const step = direction || this.shortestDirection(nextIndex);

      this.index = nextIndex;
      this.clearPeek(incoming);
      this.updateSlides();

      if (!instant && typeof incoming.animate === 'function') {
        this.animateTransition(current, incoming, step, offset);
      } else {
        current.style.transform = '';
        incoming.style.transform = '';
      }

      if (announce) this.announce();
      this.preload(this.index + step);
      this.schedule();
    }

    shortestDirection(nextIndex) {
      const count = this.slides.length;
      const forward = (nextIndex - this.index + count) % count;
      return forward <= count - forward ? 1 : -1;
    }

    animateTransition(current, incoming, step, offset) {
      const width = this.viewport.clientWidth || 1;
      const reduced = this.reducedMotion.matches;
      const progress = Math.min(Math.abs(offset) / width, 0.9);
      const duration = reduced ? 150 : Math.max(200, Math.round(this.duration * (1 - progress)));
      const timing = { duration, easing: 'cubic-bezier(0.25, 0.8, 0.25, 1)' };

      current.classList.add('is-leaving');
      incoming.classList.add('is-entering');

      let animations;
      if (reduced || this.mode === 'fade') {
        animations = [incoming.animate([{ opacity: 0 }, { opacity: 1 }], timing)];
      } else {
        animations = [
          incoming.animate(
            [{ transform: `translateX(calc(${step * 100}% + ${offset}px))` }, { transform: 'translateX(0)' }],
            timing
          ),
          current.animate(
            [{ transform: `translateX(${offset}px)` }, { transform: `translateX(${-step * 100}%)` }],
            timing
          ),
        ];
      }

      this.runAnimations(animations, () => {
        current.classList.remove('is-leaving');
        incoming.classList.remove('is-entering');
        current.style.transform = '';
        incoming.style.transform = '';
      });
    }

    runAnimations(animations, cleanup) {
      const id = ++this.animationId;
      this.animations = animations;
      this.pendingCleanup = cleanup;
      Promise.all(animations.map((animation) => animation.finished))
        .then(() => {
          if (id === this.animationId) this.completeAnimations();
        })
        .catch(() => {});
    }

    completeAnimations() {
      const cleanup = this.pendingCleanup;
      const animations = this.animations;
      this.animationId++;
      this.pendingCleanup = null;
      this.animations = [];
      animations.forEach((animation) => animation.cancel());
      if (cleanup) cleanup();
    }

    updateSlides() {
      this.slides.forEach((slide, index) => {
        const active = index === this.index;
        slide.classList.toggle('is-active', active);
        if (active) {
          slide.removeAttribute('aria-hidden');
          slide.removeAttribute('inert');
        } else {
          slide.setAttribute('aria-hidden', 'true');
          slide.setAttribute('inert', '');
        }
      });

      this.dots.forEach((dot, index) => {
        if (index === this.index) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });
    }

    announce() {
      if (!this.status) return;
      const slide = this.slides[this.index];
      const heading = slide.querySelector('.peppers-hero-slider__heading');
      const parts = [`${this.dataset.labelSlide} ${this.index + 1} ${this.dataset.labelOf} ${this.slides.length}`];
      if (heading) parts.push(heading.textContent.trim());
      this.status.textContent = parts.join(': ');
    }

    /* Lädt genau ein Folgebild vor. Ein lazy <img> auf eager umzustellen lässt den Browser selbst die
       passende <source> (Desktop/Mobil) wählen, ohne doppelte Requests. */
    preload(target) {
      const count = this.slides.length;
      const slide = this.slides[((target % count) + count) % count];
      const image = slide && slide.querySelector('img[loading="lazy"]');
      if (image) image.loading = 'eager';
    }

    /* Autoplay */

    schedule() {
      clearTimeout(this.timer);
      this.timer = null;
      if (!this.autoplay || this.pauseReasons.size > 0) return;
      this.timer = setTimeout(() => this.goTo(this.index + 1, { direction: 1 }), this.speed);
    }

    pause(reason) {
      this.pauseReasons.add(reason);
      this.schedule();
    }

    resume(reason) {
      if (!this.pauseReasons.delete(reason)) return;
      this.schedule();
    }

    togglePause() {
      if (this.pauseReasons.has('user')) {
        this.pauseReasons.delete('user');
      } else {
        this.pauseReasons.add('user');
      }
      this.updatePauseButton();
      this.schedule();
    }

    updatePauseButton() {
      if (!this.pauseButton) return;
      const paused = this.pauseReasons.has('user');
      this.pauseButton.classList.toggle('is-paused', paused);
      this.pauseButton.setAttribute('aria-label', paused ? this.dataset.labelPlay : this.dataset.labelPause);
    }

    /* Tastatur */

    onKeydown(event) {
      if (event.target.closest('input, textarea, select, [contenteditable]')) return;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.navigate(this.index - 1, -1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.navigate(this.index + 1, 1);
      }
    }

    /* Swipe (Touch/Stift). Maus-Navigation erfolgt über Pfeile und Punkte. */

    onPointerDown(event) {
      if (event.pointerType === 'mouse' || !event.isPrimary) return;
      if (event.target.closest('a, button')) return;
      this.drag = {
        id: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        lastX: event.clientX,
        lastTime: event.timeStamp,
        velocity: 0,
        dx: 0,
        active: false,
      };
    }

    onPointerMove(event) {
      const drag = this.drag;
      if (!drag || event.pointerId !== drag.id) return;

      const dx = event.clientX - drag.startX;
      const dy = event.clientY - drag.startY;

      if (!drag.active) {
        if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
        if (Math.abs(dy) >= Math.abs(dx)) {
          this.drag = null;
          return;
        }
        drag.active = true;
        this.completeAnimations();
        this.pause('drag');
        try {
          this.viewport.setPointerCapture(event.pointerId);
        } catch (error) {}
      }

      const elapsed = Math.max(1, event.timeStamp - drag.lastTime);
      drag.velocity = (event.clientX - drag.lastX) / elapsed;
      drag.lastX = event.clientX;
      drag.lastTime = event.timeStamp;
      drag.dx = dx;

      if (this.mode === 'slide' && !this.reducedMotion.matches) this.renderDrag(dx);
    }

    renderDrag(dx) {
      const count = this.slides.length;
      const step = dx < 0 ? 1 : -1;
      const neighbor = this.slides[(this.index + step + count) % count];
      const current = this.slides[this.index];

      if (this.peek && this.peek !== neighbor) this.clearPeek();
      this.peek = neighbor;
      neighbor.classList.add('is-peek');
      current.style.transform = `translateX(${dx}px)`;
      neighbor.style.transform = `translateX(calc(${step * 100}% + ${dx}px))`;
    }

    clearPeek(keep) {
      if (!this.peek) return;
      this.peek.classList.remove('is-peek');
      if (this.peek !== keep) this.peek.style.transform = '';
      this.peek = null;
    }

    onPointerUp(event, cancelled = false) {
      const drag = this.drag;
      if (!drag || event.pointerId !== drag.id) return;
      this.drag = null;
      if (!drag.active) return;

      const width = this.viewport.clientWidth || 1;
      const step = drag.dx < 0 ? 1 : -1;
      const flick = Math.abs(drag.dx) > 20 && Math.abs(drag.velocity) > 0.4;
      const commit = !cancelled && (Math.abs(drag.dx) > width * 0.15 || flick);

      if (commit) {
        this.goTo(this.index + step, { direction: step, announce: true, offset: drag.dx });
      } else {
        this.snapBack(drag.dx);
      }

      this.resume('drag');
    }

    snapBack(dx) {
      const current = this.slides[this.index];
      const neighbor = this.peek;
      if (!neighbor || dx === 0 || typeof current.animate !== 'function') {
        current.style.transform = '';
        this.clearPeek();
        return;
      }

      const step = dx < 0 ? 1 : -1;
      const timing = { duration: 200, easing: 'ease-out' };
      this.runAnimations(
        [
          current.animate([{ transform: `translateX(${dx}px)` }, { transform: 'translateX(0)' }], timing),
          neighbor.animate(
            [{ transform: `translateX(calc(${step * 100}% + ${dx}px))` }, { transform: `translateX(${step * 100}%)` }],
            timing
          ),
        ],
        () => {
          current.style.transform = '';
          this.clearPeek();
        }
      );
    }

    /* Theme Editor */

    onBlockSelect(event) {
      const index = this.slides.indexOf(event.target);
      if (index === -1) return;
      this.pause('editor');
      this.goTo(index, { instant: true });
    }

    onBlockDeselect(event) {
      if (this.slides.indexOf(event.target) === -1) return;
      this.resume('editor');
    }
  }

  customElements.define('peppers-hero-slider', PeppersHeroSlider);
}
